import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  useColorModeValue,
  useBreakpointValue,
  Button,
  Icon,
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import {
  SettingsIcon,
  CalendarIcon,
  ChatIcon,
  QuestionIcon,
  InfoIcon,
  StarIcon,
  ExternalLinkIcon,
  PhoneIcon,
} from '@chakra-ui/icons';
import { getUserDetail, getPersonalDetail, getTenantInfo } from '../services/api';

const StepCard = ({ 
  stepNumber,
  title, 
  description, 
  icon, 
  to, 
  buttonText,
  isMain = false,
  isExternal = false,
  hasTwoButtons = false,
  secondButtonText = '',
  secondButtonTo = ''
}) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const mainCardBg = useColorModeValue('yellow.50', 'yellow.900');
  const mainBorderColor = useColorModeValue('yellow.300', 'yellow.600');

  return (
    <Box
      bg={cardBg}
      borderWidth={isMain ? "2px" : "1px"}
      borderColor={isMain ? mainBorderColor : borderColor}
      borderRadius="xl"
      p={8}
      transition="all 0.3s"
      _hover={{
        transform: isMain ? 'translateY(-4px) scale(1.02)' : 'translateY(-2px)',
        shadow: isMain ? 'xl' : 'lg',
        bg: hoverBg,
        borderColor: isMain ? 'yellow.400' : 'gray.400',
      }}
      minH="300px"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      position="relative"
    >
      {/* ステップ番号 */}
      <Box
        position="absolute"
        top="-12px"
        left="20px"
        bg={isMain ? "yellow.500" : "gray.500"}
        color="white"
        borderRadius="lg"
        px={3}
        py={1}
        fontSize="xs"
        fontWeight="semibold"
        letterSpacing="wider"
        zIndex="1"
        boxShadow="md"
      >
        STEP {stepNumber}
      </Box>
      
      {/* コンテンツ */}
      <Box mt={6}>
        <HStack spacing={4} mb={5}>
          <Box
            p={2}
            bg="gray.100"
            borderRadius="lg"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={icon} boxSize={isMain ? 7 : 6} color={isMain ? "yellow.600" : "gray.600"} />
          </Box>
          <Heading size="md" color="gray.700" fontWeight="semibold">
            {title}
          </Heading>
        </HStack>
        
        <Text color="gray.600" fontSize="sm" lineHeight="1.6" mb={5}>
          {description}
        </Text>
      </Box>
      
      {/* アクションボタン */}
      <Box>
        {hasTwoButtons ? (
          <VStack spacing={2}>
            <Button
              as={RouterLink}
              to={to}
              size="sm"
              colorScheme={isMain ? "yellow" : "gray"}
              variant="outline"
              width="full"
              _hover={{
                bg: isMain ? 'yellow.500' : 'gray.500',
                color: 'white',
                borderColor: isMain ? 'yellow.500' : 'gray.500',
              }}
            >
              {buttonText}
            </Button>
            <Button
              as={RouterLink}
              to={secondButtonTo}
              size="sm"
              colorScheme={isMain ? "yellow" : "gray"}
              variant="outline"
              width="full"
              _hover={{
                bg: isMain ? 'yellow.500' : 'gray.500',
                color: 'white',
                borderColor: isMain ? 'yellow.500' : 'gray.500',
              }}
            >
              {secondButtonText}
            </Button>
          </VStack>
        ) : (
          <Button
            as={isExternal ? 'a' : RouterLink}
            to={!isExternal ? to : undefined}
            href={isExternal ? to : undefined}
            target={isExternal ? '_blank' : undefined}
            rel={isExternal ? 'noopener noreferrer' : undefined}
            size="sm"
            colorScheme={isMain ? "yellow" : "gray"}
            variant={isMain ? "solid" : "outline"}
            width="full"
            bg={isMain ? "yellow.700" : undefined}
            color={isMain ? "white" : undefined}
            _hover={{
              bg: isMain ? 'yellow.800' : 'gray.500',
              color: isMain ? 'white' : 'white',
              borderColor: isMain ? 'yellow.800' : 'gray.500',
            }}
          >
            {buttonText}
          </Button>
        )}
      </Box>
    </Box>
  );
};

const NavigationCard = ({ 
  title, 
  description, 
  icon, 
  to, 
  buttonText,
  isExternal = false 
}) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  return (
    <Box
      as={isExternal ? 'a' : RouterLink}
      to={!isExternal ? to : undefined}
      href={isExternal ? to : undefined}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      p={6}
      transition="all 0.2s"
      _hover={{
        transform: 'translateY(-2px)',
        shadow: 'lg',
        bg: hoverBg,
        borderColor: 'gray.400',
      }}
      minH="220px"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      cursor="pointer"
    >
      {/* コンテンツ */}
      <Box>
        <HStack spacing={3} mb={4}>
          <Icon as={icon} boxSize={6} color="gray.600" />
          <Heading size="md" color="gray.700">
            {title}
          </Heading>
        </HStack>
        
        <Text color="gray.600" fontSize="sm" lineHeight="1.6" mb={4}>
          {description}
        </Text>
      </Box>
      
      {/* アクションボタン */}
      <Box>
        <Button
          size="sm"
          colorScheme="gray"
          variant="outline"
          width="full"
          _hover={{
            bg: 'gray.500',
            color: 'white',
            borderColor: 'gray.500',
          }}
        >
          {buttonText}
        </Button>
      </Box>
    </Box>
  );
};

const UserTopPage = () => {
  const { currentUser, isFunctionEnabled } = useAuth();
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const [userDisplayName, setUserDisplayName] = useState('');
  const [tenantInfo, setTenantInfo] = useState(null);

  // ユーザー情報とテナント情報を取得
  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!currentUser) return;
      
      try {
        const userData = await getUserDetail(currentUser.uid);
        const personalData = await getPersonalDetail(currentUser.uid);
        
        // 表示名、氏名、メールアドレスの優先順で表示名を決定
        const displayName = personalData.nickname || userData.name || currentUser.email?.split('@')[0] || 'ユーザー';
        setUserDisplayName(displayName);
      } catch (error) {
        console.error('ユーザー情報の取得に失敗:', error);
        setUserDisplayName(currentUser.email?.split('@')[0] || 'ユーザー');
      }
    };

    const fetchTenantInfo = async () => {
      try {
        const info = await getTenantInfo();
        setTenantInfo(info);
      } catch (error) {
        console.error('テナント情報の取得に失敗:', error);
      }
    };

    fetchUserInfo();
    fetchTenantInfo();
  }, [currentUser]);

  const stepItems = [
    {
      stepNumber: 1,
      title: '使い方ガイド',
      description: '本サイトの使い方を詳しく解説しています。まずはご一読ください。',
      icon: QuestionIcon,
      to: '/guide',
      buttonText: '確認する',
    },
    {
      stepNumber: 2,
      title: '選び方ガイド・設備選択・見積作成',
      description: '住宅設備・建材・間取り等の仕様を選択できます。仕様を選んで理想の住まいをイメージしましょう。',
      icon: SettingsIcon,
      to: '/equipment',
      buttonText: '早速開始する',
      isMain: true,
    },
    {
      stepNumber: 3,
      title: 'ご相談・来店予約',
      description: '見積作成後や途中でお困りのことがあれば、チャットや対面でのご相談を承っております。どちらもご気軽にご利用ください。',
      icon: ChatIcon,
      to: '/chat',
      buttonText: 'チャットで相談する',
      hasTwoButtons: true,
      secondButtonText: '来店予約する',
      secondButtonTo: '/visit-reservation',
    },
  ];

  const otherItems = [
    {
      title: 'お気に入り画像',
      description: '気に入った設備の画像を保存して、当社スタッフと共有できます。どういった住まいが理想か是非お聞かせください。お客様の好みに沿ったご提案ができます。',
      icon: StarIcon,
      to: '/like-images',
      buttonText: '共有する',
    },
    {
      title: 'ユーザー情報',
      description: 'アカウント情報の確認や変更ができます。パスワード変更やLINE連携やご家族の登録もこちらから行えます。',
      icon: InfoIcon,
      to: '/user-info',
      buttonText: '確認する',
    },
    // 会社概要リンクを追加
    ...(tenantInfo?.company_intro_url ? [{
      title: '会社概要',
      description: '当社の会社概要や実績をご覧いただけます。',
      icon: ExternalLinkIcon,
      to: tenantInfo.company_intro_url,
      buttonText: '確認する',
      isExternal: true,
    }] : []),
  ];

  const columns = useBreakpointValue({ base: 1, md: 2, lg: 3 });

  return (
    <Box bg={pageBg} minH="100vh" py={8}>
      <Container maxW="container.xl">
        <VStack spacing={8} align="stretch">
          {/* ヘッダー */}
          <Box textAlign="center" mb={8}>
            <Heading size="lg" color="gray.700" mb={4}>
              ホーム
            </Heading>
            <Text color="yellow.700" fontSize="xl" fontWeight="bold" mb={2}>
              こんにちは、{userDisplayName}さん
            </Text>
            <Text color="gray.600" fontSize="lg">
              お客様の理想の住まいづくりをサポートします
            </Text>
          </Box>

                     {/* ステップカード */}
           <Box mb={12}>
             <Heading size="md" color="gray.700" mb={6} textAlign="center">
               ご利用の流れ
             </Heading>
             <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
               {stepItems.map((item, index) => (
                 <StepCard
                   key={index}
                   stepNumber={item.stepNumber}
                   title={item.title}
                   description={item.description}
                   icon={item.icon}
                   to={item.to}
                   buttonText={item.buttonText}
                   isMain={item.isMain}
                   isExternal={item.isExternal}
                   hasTwoButtons={item.hasTwoButtons}
                   secondButtonText={item.secondButtonText}
                   secondButtonTo={item.secondButtonTo}
                 />
               ))}
             </SimpleGrid>
           </Box>

           {/* その他の機能 */}
           <Box>
             <Heading size="md" color="gray.700" mb={6} textAlign="center">
               その他のページ
             </Heading>
             <SimpleGrid columns={columns} spacing={6}>
               {otherItems.map((item, index) => (
                 <NavigationCard
                   key={index}
                   title={item.title}
                   description={item.description}
                   icon={item.icon}
                   to={item.to}
                   buttonText={item.buttonText}
                   isExternal={item.isExternal}
                 />
               ))}
             </SimpleGrid>
           </Box>

          {/* 追加情報 */}
          <Box
            bg="white"
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="lg"
            p={6}
            mt={8}
            textAlign="center"
          >
            <Text color="gray.600" fontSize="sm" mb={2}>
              ご不明な点がございましたら、お気軽にご相談ください。
            </Text>
            {tenantInfo?.contact_phone && (
              <HStack spacing={2} justify="center" align="center">
                <PhoneIcon color="gray.600" boxSize={4} />
                <Text color="gray.600" fontSize="sm" fontWeight="bold">
                  {tenantInfo.contact_phone}
                </Text>
              </HStack>
            )}
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default UserTopPage;
